import { Link, NavLink, useNavigate } from "react-router-dom";
import { GitCompareArrows, Compass, LayoutDashboard, LogOut, LogIn, ShieldCheck, Sparkles } from "lucide-react";
import { useCompare } from "@/context/CompareContext";
import { useAuth } from "@/context/AuthContext";

const navItems = [
  { to: "/programs", label: "Directory" },
  { to: "/official-links", label: "Official Links" },
  { to: "/niches", label: "Niches" },
  { to: "/membership", label: "Members Club" },
  { to: "/rewards", label: "Rewards" },
  { to: "/leaderboard", label: "Leaderboard" },
  { to: "/beginners", label: "Beginner's Hub" },
  { to: "/calculators", label: "Calculators" },
  { to: "/adcp", label: "AdCP Tools" },
  { to: "/get-featured", label: "Get Featured" },
  { to: "/about", label: "About" },
];

export const Header = () => {
  const { items } = useCompare();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-2">
          <Compass className="w-6 h-6 text-emerald-600" strokeWidth={2.2} />
          <span className="font-heading font-semibold text-lg tracking-tight">
            Social<span className="text-emerald-600">Flats</span>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium rounded-full transition-colors duration-200 ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="header-admin-link"
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full bg-indigo-600 text-white hover:bg-indigo-500 transition-colors duration-200">
                  <ShieldCheck className="w-4 h-4" />
                  <span className="hidden sm:inline">Admin</span>
                </Link>
              )}
              {user.adult_opt_in && (
                <Link to="/adult" data-testid="header-adult-link" aria-label="Adult section"
                  title="Adult (18+) affiliates"
                  className="w-9 h-9 rounded-full bg-slate-900 text-rose-400 hover:text-rose-300 flex items-center justify-center border border-slate-800">
                  <span className="text-xs font-black tracking-wider">18+</span>
                </Link>
              )}
              <Link to="/dashboard" data-testid="header-dashboard-link"
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full bg-slate-900 text-white hover:bg-slate-700 transition-colors duration-200">
                <LayoutDashboard className="w-4 h-4" />
                <span className="hidden sm:inline">Dashboard</span>
              </Link>
              <button onClick={async () => { await logout(); navigate("/"); }} data-testid="header-logout-button" aria-label="Log out"
                className="p-2.5 rounded-full border border-slate-200 text-slate-500 hover:text-slate-900 hover:border-slate-400 transition-colors duration-200">
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : user === false ? (
            <Link to="/login" data-testid="header-login-link"
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full border border-slate-300 text-slate-700 hover:border-slate-900 transition-colors duration-200">
              <LogIn className="w-4 h-4" />
              <span className="hidden sm:inline">Log in</span>
            </Link>
          ) : null}
          <Link
            to="/compare"
            data-testid="header-compare-link"
            className="relative flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full bg-emerald-600 text-white hover:bg-emerald-500 transition-colors duration-200"
          >
            <GitCompareArrows className="w-4 h-4" />
            <span className="hidden sm:inline">Compare</span>
            {items.length > 0 && (
              <span data-testid="compare-count-badge" className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-900 text-white text-xs flex items-center justify-center">
                {items.length}
              </span>
            )}
          </Link>
        </div>
      </div>
      <nav className="md:hidden flex items-center gap-1 px-4 pb-2 overflow-x-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors duration-200 ${
                isActive ? "bg-slate-900 text-white" : "text-slate-600 bg-slate-100"
              }`
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
    </header>
  );
};
